import { useEffect, useRef, type ReactNode } from 'react'
import { Platform } from 'react-native'
import { usePathname, useRootNavigationState, useRouter, type Href } from 'expo-router'
import { Spinner, YStack } from 'tamagui'

import { useAuth } from './useAuth'

/**
 * Gate: no session → login screen; session (or dev-fallback) → app.
 * Shows a spinner while the saved session is restored.
 *
 * We must NOT return <Redirect> in place of the children: the children ARE the
 * root <Stack> navigator (see app/_layout.tsx), so swapping it out unmounts the
 * navigator and expo-router throws "Attempted to navigate before mounting the
 * Root Layout". Instead we always render the navigator and redirect
 * imperatively from an effect, gated on `useRootNavigationState().key` so we
 * never navigate before the navigator is mounted.
 *
 * Прямая ссылка (например `/processes/:id`, переданная коллеге) переживает логин: путь
 * уезжает в `/login?next=…` и после входа восстанавливается. Только свой абсолютный путь —
 * `//host` и `https://…` отбрасываются (open redirect).
 *
 * `next` НЕ читается хуком useGlobalSearchParams: AuthGate живёт ВЫШЕ навигатора, и подписка
 * на параметры маршрута оттуда зацикливает рендер — «Maximum update depth exceeded», весь
 * портал уходил в ErrorBoundary («Что-то пошло не так») на любом экране (тест-стенд, 03.09.2026).
 * Путь нужен ровно в момент редиректа, подписка на него не нужна: запоминаем свой же редирект
 * в ref, а на вебе читаем `?next=` из адресной строки — так ссылка переживает и перезагрузку
 * страницы на экране входа.
 */
export const safeNext = (v: unknown): string => (typeof v === 'string' && /^\/(?!\/)/.test(v) ? v : '/')

/**
 * Пути без входа в портал. `/s/<токен>` — страница подписания договора контрагентом: учётной
 * записи у него нет и быть не должно, пропуск — сам токен в ссылке. `/reset-password` — по
 * ней приходят из письма ИМЕННО потому, что войти не могут. Логин на этих путях был бы
 * тупиком, а не защитой, поэтому гейт их не трогает — ни в одну, ни в другую сторону.
 *
 * Для `/reset-password` это ещё и единственный способ не потерять токен: редирект на логин
 * уносит только путь (`next`), query отбрасывается — ссылка из письма стала бы бесполезной.
 */
export const isPublicPath = (pathname: string): boolean =>
  pathname === '/login' ||
  pathname === '/s' ||
  pathname.startsWith('/s/') ||
  pathname === '/reset-password' ||
  // Кабинет внешних: его вход, анкета на доступ и колбэк браузерного флоу. Сессии портала
  // у этих людей нет и не будет, поэтому редирект на корпоративный логин был бы тупиком.
  pathname === '/cabinet/login' ||
  pathname === '/cabinet/signup' ||
  pathname.startsWith('/cabinet/auth/')

/** Маршруты кабинета внешнего пользователя. */
export const isCabinetPath = (pathname: string): boolean =>
  pathname === '/cabinet' || pathname.startsWith('/cabinet/')

export function AuthGate({ children }: { children: ReactNode }) {
  const { isLoading, isAuthenticated, sessionKind } = useAuth()
  const pathname = usePathname()
  const router = useRouter()
  const navState = useRootNavigationState()
  // Путь, с которого увели на логин; на вебе дублируется в `?next=` (переживает reload).
  const nextRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!navState?.key) return // root navigator not mounted yet
    if (isLoading) return // still restoring the saved session
    if (isPublicPath(pathname) && pathname !== '/login') return

    /**
     * Контуры не смешиваются. Внешний (поставщик, турагент) в корпоративном портале не имеет
     * дела: его токен там всё равно получит 403 от гейта изоляции на бэке, и человек увидел
     * бы пустой портал с ошибками вместо своего кабинета. Обратное тоже верно: сотрудник в
     * кабинете получит 403 «доступ не выдан» — его карточки участника не существует.
     */
    if (isAuthenticated && sessionKind === 'cabinet' && !isCabinetPath(pathname)) {
      router.replace('/cabinet' as Href)
      return
    }
    if (isAuthenticated && sessionKind === 'portal' && isCabinetPath(pathname)) {
      router.replace('/')
      return
    }
    // Вход один на два контура, вкладка выбирает realm: внешнего ведём сразу на неё, иначе он
    // упёрся бы в корпоративную форму, которая его пароль не знает.
    if (!isAuthenticated && isCabinetPath(pathname)) {
      router.replace('/login?tab=partner' as Href)
      return
    }

    if (!isAuthenticated && pathname !== '/login') {
      // ponytail: query не сохраняем (/org?focus=… вернётся на /org) — хватает пути документа
      nextRef.current = pathname === '/' ? undefined : pathname
      router.replace(
        nextRef.current ? { pathname: '/login', params: { next: nextRef.current } } : '/login',
      )
    } else if (isAuthenticated && pathname === '/login') {
      const fromUrl =
        Platform.OS === 'web' && typeof location !== 'undefined'
          ? new URLSearchParams(location.search).get('next')
          : null
      const to = safeNext(fromUrl ?? nextRef.current)
      nextRef.current = undefined
      router.replace(to as Href)
    }
  }, [navState?.key, isLoading, isAuthenticated, sessionKind, pathname, router])

  if (isLoading) {
    return (
      <YStack flex={1} alignItems="center" justifyContent="center" backgroundColor="$bgLayout">
        <Spinner size="large" color="$primary" />
      </YStack>
    )
  }

  return <>{children}</>
}
